import React, { useState, useEffect, useCallback, useRef } from 'react';
import FileUpload from './components/FileUpload';
import LinkTable from './components/LinkTable';
import Stats from './components/Stats';
import ThemeToggle from './components/ThemeToggle';
import ExportButtons from './components/ExportButtons';
import ProcessingProgress from './components/ProcessingProgress';
import LoginForm from './components/LoginForm';
import PasswordModal from './components/PasswordModal';
import ConfirmModal from './components/ConfirmModal';
import SessionsModal from './components/SessionsModal';
import AdminPanel from './components/AdminPanel';
import AcceptInvite from './components/AcceptInvite';
import { Card, CardHeader, CardContent } from './components/ui/Card';
import { Button } from './components/ui/Button';
import { Badge } from './components/ui/Badge';
import {
  fetchLinks, fetchStatus, uploadFile, clearData, login, clearAuth, onAuthLost,
  resetStatus, initAuth, startImpersonation, stopImpersonation, onImpersonationEnded,
} from './api';
import api from './api/client';
import { useToast } from './components/Toast';

const fetchMe = () => api.get('/users/me').then((r) => r.data);

/** Начальная тема: сохранённый выбор → prefers-color-scheme → dark. */
const getInitialDarkMode = () => {
  try {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
  } catch { /* localStorage недоступен */ }
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return true;
};

/** Пункт мобильного меню действий (высота ≥44px для тач-целей). */
const MenuItem = ({ icon, label, onClick, disabled = false, destructive = false }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    disabled={disabled}
    className={
      'flex w-full items-center gap-lg px-xl min-h-[44px] text-body-sm text-left ' +
      'transition-colors duration-150 cursor-pointer ' +
      'disabled:opacity-45 disabled:cursor-not-allowed disabled:pointer-events-none ' +
      (destructive
        ? 'text-destructive hover:bg-destructive-soft'
        : 'text-text hover:bg-muted')
    }
  >
    <i className={`bi ${icon} w-xl text-center`} aria-hidden="true" />
    {label}
  </button>
);

function App() {
  // ── Auth ──
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [authError, setAuthError] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [username, setUsername] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // ── Data ──
  const [links, setLinks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalLinks, setTotalLinks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);

  // ── UI ──
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const [clearing, setClearing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const pollingRef = useRef(null);
  const pollGenRef = useRef(0);
  const adminInfoRef = useRef(null);
  const toast = useToast();

  // ── Theme ──
  const toggleTheme = useCallback(() => setDarkMode((prev) => !prev), []);
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
    try {
      localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    } catch { /* localStorage недоступен */ }
  }, [darkMode]);

  // ── Auth check + lost listener ──
  // Silent session restore: initAuth() exchanges the httpOnly refresh cookie
  // for a fresh access token, so a page reload does not log the user out.
  useEffect(() => {
    (async () => {
      const auth = await initAuth();
      if (auth) {
        // Restore user info from /users/me
        try {
          const me = await fetchMe();
          setUsername(me.username);
          setUserRole(me.role);
        } catch {
          // Token valid but /users/me failed — still ok
        }
        setAuthenticated(true);
      }
      setAuthChecking(false);
    })();
  }, []);

  useEffect(() => {
    const unsub = onAuthLost(() => {
      setAuthenticated(false);
      setImpersonating(false);
      adminInfoRef.current = null;
      setLinks([]);
      setTotalLinks(0);
      setProcessingStatus(null);
    });
    return unsub;
  }, []);

  const handleLogin = useCallback(async (username, password) => {
    setAuthError('');
    try {
      const result = await login(username, password);
      setAuthenticated(true);
      setUserRole(result.role || 'user');
      setUsername(result.username || username);
    } catch {
      setAuthError('Неверный логин или пароль');
    }
  }, []);

  const handleLogout = useCallback(() => {
    clearAuth();
    setAuthenticated(false);
    setUsername('');
    setUserRole('user');
    setLinks([]);
    setTotalLinks(0);
    setProcessingStatus(null);
  }, []);

  const handlePasswordChanged = useCallback(() => {
    toast('Пароль изменён. Войдите заново.', 'info');
    clearAuth();
  }, [toast]);

  // ── Data fetching ──
  const loadLinks = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await fetchLinks(page);
      setLinks(data.items);
      setCurrentPage(data.page);
      setTotalPages(data.total_pages);
      setTotalLinks(data.total);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleLogout();
        return;
      }
      console.error('Ошибка загрузки:', error?.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  // Stops the interval and invalidates in-flight responses of older ticks.
  const stopPolling = useCallback(() => {
    pollGenRef.current += 1;
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    const gen = pollGenRef.current;
    let inFlight = false; // never overlap ticks
    pollingRef.current = setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await fetchStatus();
        if (gen !== pollGenRef.current) return; // stale tick — ignore
        setProcessingStatus(data);
        if (data.status === 'completed') {
          stopPolling();
          setUploading(false);
          await loadLinks(1); // exactly once, after polling has stopped
          return;
        }
        if (data.status === 'error') {
          stopPolling();
          setUploading(false);
          return;
        }
        if (data.status === 'processing') {
          if (data.stage === 'cleaning' || (data.stage === 'loading' && data.progress <= 5)) {
            setLinks([]); setTotalLinks(0); setCurrentPage(1); setTotalPages(0);
          }
        }
      } catch {
        if (gen === pollGenRef.current) {
          stopPolling();
          setUploading(false);
        }
      } finally {
        inFlight = false;
      }
    }, 1000);
  }, [stopPolling, loadLinks]);

  const handleResetPipeline = useCallback(async () => {
    try {
      await resetStatus();
      stopPolling();
      setProcessingStatus(null);
      setUploading(false);
      // Re-sync with the server instead of blindly clearing local state —
      // a reset after "completed" keeps the already-analyzed data on the server.
      await loadLinks(1);
    } catch (err) { console.error('Reset failed:', err); }
  }, [stopPolling, loadLinks]);

  const handleFileUpload = useCallback(async (file) => {
    setUploading(true); setProcessingStatus(null);
    setLinks([]); setTotalLinks(0); setCurrentPage(1); setTotalPages(0);
    try {
      await uploadFile(file);
      startPolling();
    } catch (error) {
      if (error?.response?.status === 400) {
        toast(error?.response?.data?.detail || 'Ошибка загрузки файла', 'warning');
      } else {
        toast('Ошибка загрузки: ' + (error?.response?.data?.detail || error.message), 'error');
      }
      setUploading(false);
    }
  }, [startPolling, toast]);

  const handlePageChange = useCallback((page) => { loadLinks(page); }, [loadLinks]);

  // ── Clear data ──
  const handleClearData = useCallback(async () => {
    setShowClearConfirm(false);
    setClearing(true);
    // Stop polling *before* clearing to prevent a stale poll response from
    // restoring old data after the state has been reset.
    stopPolling();
    try {
      await clearData();
      setLinks([]);
      setTotalLinks(0);
      setCurrentPage(1);
      setTotalPages(0);
      setProcessingStatus(null);
      setUploading(false);
    } catch (error) {
      if (error?.response?.status === 400) {
        toast(error?.response?.data?.detail || 'Нельзя очистить данные во время обработки', 'warning');
      } else {
        toast('Ошибка очистки: ' + (error?.response?.data?.detail || error.message), 'error');
      }
    } finally {
      setClearing(false);
    }
  }, [toast, stopPolling]);

  // ── Impersonation ──
  const handleImpersonate = useCallback((token, uname) => {
    adminInfoRef.current = { username, userRole };
    startImpersonation(token);
    setImpersonating(true);
    setUsername(uname);
    setUserRole('user');
    stopPolling();
    setProcessingStatus(null);
    setLinks([]);
    setTotalLinks(0);
    loadLinks(1);
  }, [username, userRole, stopPolling, loadLinks]);

  const exitImpersonation = useCallback(() => {
    // Restores the admin access token and fires 'auth:impersonation-ended'.
    stopImpersonation();
  }, []);

  useEffect(() => {
    const unsub = onImpersonationEnded(() => {
      setImpersonating(false);
      const info = adminInfoRef.current;
      if (info) {
        setUsername(info.username);
        setUserRole(info.userRole);
        adminInfoRef.current = null;
      }
      stopPolling();
      setProcessingStatus(null);
      setLinks([]);
      setTotalLinks(0);
      loadLinks(1);
    });
    return unsub;
  }, [stopPolling, loadLinks]);

  // ── Init ──
  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchStatus();
        if (cancelled) return;
        setProcessingStatus(status);
        if (status.status === 'processing') { setUploading(true); startPolling(); }
        else await loadLinks();
      } catch { /* сервер не готов */ }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated]);

  // ── Invite route (BEFORE auth — public page) ──
  if (window.location.pathname.startsWith('/invite/')) {
    const token = window.location.pathname.split('/invite/')[1];
    return <AcceptInvite token={token} />;
  }

  // ── Auth checking / Login ──
  if (authChecking) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="flex flex-col items-center gap-lg animate-fade-in">
          <div
            className="h-9 w-9 rounded-pill border-2 border-border border-t-primary animate-spin"
            aria-hidden="true"
          />
          <p className="eyebrow">Проверка доступа</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <LoginForm onLogin={handleLogin} error={authError} />;
  }

  // «Сброс» доступен только когда пайплайн активен (та же логика, что была).
  const resetDisabled = !uploading && !(processingStatus && processingStatus.status !== 'idle');
  const closeMenu = () => setMenuOpen(false);

  // ── Main app ──
  return (
    <div className="min-h-screen bg-surface text-text">
      {/* ── Toolbar ── */}
      <header className="fixed top-0 inset-x-0 z-40 h-12 bg-surface-raised border-b border-border shadow-sm">
        <div className="relative max-w-app mx-auto h-full px-xl flex items-center justify-between gap-lg">
          {/* Бренд + пользователь */}
          <div className="flex items-center gap-md min-w-0">
            <i className="bi bi-soundwave text-primary text-body-md" aria-hidden="true" />
            <span className="font-mono text-body-sm font-semibold text-text-strong whitespace-nowrap">
              AdminSoundTT
            </span>
            <span className="hidden sm:block w-px h-xl bg-border" aria-hidden="true" />
            <span className="hidden sm:block font-mono text-body-xs text-text-muted truncate max-w-[160px]">
              {username || 'user'}
            </span>
            {userRole === 'admin' && (
              <Badge variant="primary" icon="bi-shield-lock" className="hidden sm:inline-flex">
                админ
              </Badge>
            )}
          </div>

          {/* Действия — десктоп (≥768px); подписи появляются с 1024px */}
          <div className="hidden md:flex items-center gap-sm">
            {userRole === 'admin' && (
              <Button size="md" icon="bi-people" title="Пользователи" onClick={() => setShowAdminPanel(true)}>
                <span className="hidden lg:inline">Админ</span>
              </Button>
            )}
            <Button size="md" icon="bi-display" title="Сессии" onClick={() => setShowSessions(true)}>
              <span className="hidden lg:inline">Сессии</span>
            </Button>
            <Button size="md" icon="bi-key" title="Сменить пароль" onClick={() => setShowPasswordModal(true)}>
              <span className="hidden lg:inline">Пароль</span>
            </Button>

            <span className="w-px h-xl bg-border mx-sm" aria-hidden="true" />

            <Button
              size="md"
              icon="bi-trash"
              title="Очистить все данные"
              disabled={clearing}
              onClick={() => setShowClearConfirm(true)}
              className="text-destructive hover:text-destructive hover:bg-destructive-soft hover:border-destructive/40"
            >
              <span className="hidden lg:inline">Очистить</span>
            </Button>
            <Button
              size="md"
              variant="danger"
              icon="bi-stop-circle"
              title="Сбросить пайплайн"
              disabled={resetDisabled}
              onClick={() => setShowResetConfirm(true)}
            >
              <span className="hidden lg:inline">Сброс</span>
            </Button>

            <span className="w-px h-xl bg-border mx-sm" aria-hidden="true" />

            <ThemeToggle darkMode={darkMode} toggleTheme={toggleTheme} />
            <Button
              size="icon-md"
              icon="bi-box-arrow-right"
              title="Выйти"
              aria-label="Выйти"
              onClick={handleLogout}
            />
          </div>

          {/* Действия — мобильные (<768px): тема + меню */}
          <div className="flex md:hidden items-center gap-sm">
            <ThemeToggle darkMode={darkMode} toggleTheme={toggleTheme} />
            <Button
              size="icon-md"
              icon="bi-three-dots-vertical"
              title="Меню действий"
              aria-label="Меню действий"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            />
          </div>

          {/* Выпадающее мобильное меню */}
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40 md:hidden" onClick={closeMenu} aria-hidden="true" />
              <div
                role="menu"
                aria-label="Действия"
                className="absolute right-xl top-full mt-sm w-64 max-w-[calc(100vw-32px)] z-50 md:hidden rounded-lg border border-border bg-surface-raised shadow-lg py-sm animate-slide-down"
              >
                {userRole === 'admin' && (
                  <MenuItem
                    icon="bi-people"
                    label="Пользователи"
                    onClick={() => { closeMenu(); setShowAdminPanel(true); }}
                  />
                )}
                <MenuItem
                  icon="bi-display"
                  label="Сессии"
                  onClick={() => { closeMenu(); setShowSessions(true); }}
                />
                <MenuItem
                  icon="bi-key"
                  label="Сменить пароль"
                  onClick={() => { closeMenu(); setShowPasswordModal(true); }}
                />
                <div className="my-sm border-t border-border" role="separator" />
                <MenuItem
                  icon="bi-trash"
                  label="Очистить данные"
                  destructive
                  disabled={clearing}
                  onClick={() => { closeMenu(); setShowClearConfirm(true); }}
                />
                <MenuItem
                  icon="bi-stop-circle"
                  label="Сбросить пайплайн"
                  destructive
                  disabled={resetDisabled}
                  onClick={() => { closeMenu(); setShowResetConfirm(true); }}
                />
                <div className="my-sm border-t border-border" role="separator" />
                <MenuItem
                  icon="bi-box-arrow-right"
                  label="Выйти"
                  onClick={() => { closeMenu(); handleLogout(); }}
                />
              </div>
            </>
          )}
        </div>
      </header>

      {/* ── Password modal ── */}
      {showPasswordModal && (
        <PasswordModal
          onClose={() => setShowPasswordModal(false)}
          onPasswordChanged={handlePasswordChanged}
        />
      )}

      {/* ── Sessions modal ── */}
      {showSessions && (
        <SessionsModal
          onClose={() => setShowSessions(false)}
          onClearAuth={() => { setShowSessions(false); clearAuth(); }}
        />
      )}

      {/* ── Admin panel ── */}
      {showAdminPanel && (
        <AdminPanel
          onClose={() => setShowAdminPanel(false)}
          onImpersonate={handleImpersonate}
        />
      )}

      {/* ── Clear confirm modal ── */}
      {showClearConfirm && (
        <ConfirmModal
          title="Очистка данных"
          message="Удалить все данные анализа? Это действие необратимо."
          confirmLabel="Удалить"
          danger
          onConfirm={handleClearData}
          onCancel={() => setShowClearConfirm(false)}
        />
      )}

      {/* ── Reset confirm modal ── */}
      {showResetConfirm && (
        <ConfirmModal
          title="Сброс пайплайна"
          message="Остановить текущую обработку и сбросить статус пайплайна? Уже проанализированные данные останутся на сервере."
          confirmLabel="Сбросить"
          danger
          onConfirm={() => { setShowResetConfirm(false); handleResetPipeline(); }}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}

      {/* ── Content ── */}
      <main className="max-w-app mx-auto px-xl pt-[64px] pb-3xl" id="main-content" tabIndex={-1}>
        {/* ── Impersonation banner ── */}
        {impersonating && (
          <div
            role="status"
            className="mb-2xl flex flex-wrap items-center justify-between gap-md rounded-md border border-accent/40 bg-accent-soft px-lg py-md animate-slide-down"
          >
            <span className="flex items-center gap-md text-body-sm font-medium text-warning">
              <i className="bi bi-person-badge" aria-hidden="true" />
              Режим имперсонации: <span className="font-mono">{username}</span>
            </span>
            <Button
              size="sm"
              icon="bi-box-arrow-left"
              onClick={exitImpersonation}
              className="bg-transparent text-warning border-accent/40 hover:text-warning hover:bg-accent-soft hover:border-accent"
            >
              Выйти из имперсонации
            </Button>
          </div>
        )}

        {/* ── Компактная шапка страницы ── */}
        <div className="mb-2xl">
          <h1 className="text-page-title font-semibold text-text-strong">Анализ музыки</h1>
          <p className="mt-xs text-body-sm text-text-muted">
            Загрузите файл со ссылками TikTok — AI-пайплайн определит жанр, контекст и пол исполнителя.
          </p>
        </div>

        <Card className="mb-2xl animate-fade-in">
          <CardHeader title="Загрузка файла" icon="bi-cloud-arrow-up" />
          <CardContent>
            <FileUpload onUpload={handleFileUpload} uploading={uploading} darkMode={darkMode} />
          </CardContent>
        </Card>

        {(uploading || (processingStatus && processingStatus.status !== 'idle')) && (
          <div className="mb-2xl">
            <ProcessingProgress status={processingStatus} />
          </div>
        )}

        <Stats darkMode={darkMode} totalLinks={totalLinks} />

        {totalLinks > 0 && (
          <Card className="animate-fade-in">
            <CardHeader
              title="Результаты анализа"
              icon="bi-table"
              actions={
                <>
                  <ExportButtons totalLinks={totalLinks} />
                  <Badge variant="primary" className="tabular">{totalLinks}</Badge>
                </>
              }
            />
            <CardContent>
              <LinkTable
                links={links}
                loading={loading}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </CardContent>
          </Card>
        )}

        {/* ── Empty state ── */}
        {!uploading && totalLinks === 0 && !loading && processingStatus?.status !== 'processing' && (
          <Card className="mt-2xl animate-fade-in">
            <CardContent className="py-3xl text-center">
              <div className="mx-auto mb-lg flex h-12 w-12 items-center justify-center rounded-pill bg-primary-soft">
                <i className="bi bi-music-note-list text-xl text-primary" aria-hidden="true" />
              </div>
              <h2 className="text-section-title font-semibold text-text-strong mb-sm">
                Пока нет результатов
              </h2>
              <p className="text-body-sm text-text-muted max-w-md mx-auto">
                Загрузите .txt файл со ссылками TikTok (по одной на строку) в форме выше —
                пайплайн скачает треки, распознает вокал и определит жанры.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

export default App;
